/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Pagination, Select, SelectItem, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Tooltip } from '@nextui-org/react';
import { IconPlus, IconSearch, IconTrash, IconEdit } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { classNames, Constants } from '../../../utils';
import { TeacherAttendeceService, TeacherTestsService } from '../../../services';
import { useSelector } from 'react-redux';
import ModalQuestionEdit from './question-edit';
import PaidAction from '../../../components/paid-action';



const MODAL_ACTION = {
  NONE: 0,
  VIEW: 1,
  EDIT: 2,
  DELETE: 3,
}

const ClassManagement = () => {
  const navigate = useNavigate();
  const [modalAction, setModalAction] = useState(MODAL_ACTION.NONE);
  const [selectedItem, setSelectedItem] = useState();
  const { canResetFilter, data, deleteEvaluation, filterBy, goToPage, isLoading, pagination, reload, schoolYears, download } = useFetchTable();

  const onSelectItem = (item, action) => {
    setSelectedItem(item);
    setModalAction(action);
  }

  const closeModal = (reloading = false) => {
    setSelectedItem(null);
    setModalAction(MODAL_ACTION.NONE);
    if (reloading) reload();
  }

  return (
    <div>
      {isLoading && (
        <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
          <Spinner />
        </div>
      )}

      <Modal
        size="lg"
        isOpen={modalAction !== MODAL_ACTION.NONE}
        onClose={() => closeModal()}
        backdrop="blur"
        scrollBehavior="outside"
      >
        <ModalContent>
          {(onClose) => {
            if (modalAction === MODAL_ACTION.VIEW) return (
              <ModalViewTests
                item={selectedItem}
                onSelectItem={onSelectItem}
              />
            );

            if (modalAction === MODAL_ACTION.EDIT) return (
              <ModalQuestionEdit
                question={selectedItem}
                onClose={onClose}
                onSuccess={() => closeModal(true)}
              />
            );

            if (modalAction === MODAL_ACTION.DELETE) return (
              <ModalDelete
                test={selectedItem}
                onClose={onClose}
                onDelete={() => {
                  deleteEvaluation(selectedItem.id);
                  closeModal(true);
                }}
              />
            );
          }}
        </ModalContent>
      </Modal>

      <Filters
        canResetFilter={canResetFilter}
        filterBy={filterBy}
        schoolYears={schoolYears}
        resetFilter={() => reload()}
      />

      <Table
        aria-label="Evaluaciones"
        color="primary"
        topContentPlacement="inside"
        topContent={
          <div className="flex flex-row justify-between items-center gap-4">
            <h3 className="text-xl font-medium">Evaluaciones creadas</h3>
            <div className='flex gap-2'>
              <PaidAction>
                <Button
                  className="pr-2 bg-green font-medium text-white"
                  endContent={<IconPlus color="white" />}
                  onPress={() => download()}
                >
                  Descargar
                </Button>
              </PaidAction>
            </div>
          </div>
        }
      >
        <TableHeader>
          <TableColumn>Nombre de la materia</TableColumn>
          <TableColumn>Código</TableColumn>
          <TableColumn>Evaluaciones</TableColumn>
          <TableColumn align="end" />
        </TableHeader>
        <TableBody items={data} emptyContent="No hay resultados">
          {(item) => {
            return (
              <TableRow key={item.id}>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item?.code}</TableCell>
                <TableCell>
                  {
                    !!item?.tests?.length && (
                      <span
                        className="cursor-pointer font-semibold text-sm text-secondary"
                        onClick={() => onSelectItem(item, MODAL_ACTION.VIEW)}
                      >
                        Ver
                      </span>
                    )
                  }
                </TableCell>
                <TableCell align='right'>
                  <Button
                    startContent={<IconPlus size={14} className='text-white' />}
                    className='bg-green text-white text-xs'
                    onPress={() => navigate('/class-management/create', { state: item })}
                  >
                    Nueva evaluación
                  </Button>
                </TableCell>
              </TableRow>
            )
          }}
        </TableBody>
      </Table>

      <div className="flex w-full justify-center mt-4">
        <Pagination
          showControls
          variant="bordered"
          page={pagination.page}
          total={pagination.pages}
          onChange={goToPage}
        />
      </div>
    </div>
  );
}

const ModalDelete = ({ test, onClose, onDelete }) => {
  return (
    <>
      <ModalHeader className="flex flex-col gap-1">Confirmación</ModalHeader>
      <ModalBody>
        <p className="text-center">¿Estás seguro de eliminar esta evaluación?. Si realizó alguna evaluación, dichos registros se eliminarán también.</p>
      </ModalBody>
      <ModalFooter className="justify-evenly">
        <Button variant="light" onPress={onClose}>Cancelar</Button>
        <Button color="danger" onPress={onDelete}>Aceptar</Button>
      </ModalFooter>
    </>
  )
}



const ModalViewTests = ({ item, onSelectItem }) => {
  const [currentLapse, setCurrentLapse] = useState(null);

  const lapses = useMemo(() => {
    const lapsesMap = new Map();

    item?.tests?.forEach(test => {
      const { school_lapse } = test;
      if (!lapsesMap.has(school_lapse?.id)) {
        lapsesMap.set(school_lapse?.id, {
          id: school_lapse?.id,
          name: school_lapse?.name,
          init_date: school_lapse?.init_date,
          tests: []
        });
      }
      lapsesMap.get(school_lapse?.id).tests.push(test);
    });

    return Array.from(lapsesMap.values()).sort((a, b) => new Date(a.init_date) - new Date(b.init_date));
  }, [item?.tests]);

  useEffect(() => {
    if (lapses.length > 0 && !currentLapse) {
      setCurrentLapse(lapses[0]);
    }
  }, [lapses, currentLapse]);

  return (
    <>
      <ModalHeader className="flex flex-col gap-1">{item?.name}</ModalHeader>
      <ModalBody className="p-0 pb-4 gap-0">
        <div className='px-6 flex items-center gap-4'>
          {
            lapses.map(lapse => (
              <div
                className={classNames(
                  'px-6 py-2 rounded-lg text-sm cursor-pointer',
                  currentLapse?.id === lapse.id ? 'bg-green text-white' : 'bg-white'
                )}
                onClick={() => setCurrentLapse(lapse)}
                key={lapse.id}
              >
                <span>{lapse.name}</span>
              </div>
            ))
          }
        </div>

        <div className='mt-5'>
          {
            currentLapse?.tests?.map((test, idx) => {

              if (test?.questions?.length === 0) return null;

              return (
                <div key={test.id}>
                  <div className='bg-secondary px-6 text-white text-sm py-2'>
                    <p>{moment(test?.date).format('D [de] MMMM [de] YYYY')}</p>
                  </div>
                  {
                    test?.questions?.map((quest, idx) => (
                      <div className='px-6 py-3' key={quest?.id}>
                        <p className='font-bold'>{quest.name}</p>
                        <p className='mt-2'>{quest.indicator?.description}</p>
                        <p className='mt-2'>{quest.observations}</p>

                        <div className='flex justify-between items-center gap-4 mt-4'>
                          <div className='text-sm font-semibold rounded-lg p-2 bg-primary-50'>
                            <span>% {quest.percentage}</span>
                          </div>

                          <div className='flex items-center gap-2'>
                            <Tooltip content="Editar">
                              <IconEdit className='cursor-pointer' onClick={() => onSelectItem({ ...quest, date: test?.date, school_lapse: test?.school_lapse, subject_id: test?.subject_id }, MODAL_ACTION.EDIT)} />
                            </Tooltip>
                            <Tooltip content="Eliminar" color="danger">
                              <IconTrash className="text-danger cursor-pointer" onClick={() => onSelectItem(quest, MODAL_ACTION.DELETE)} />
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )
            })
          }
        </div>
      </ModalBody>
    </>
  );
};


const Filters = ({ canResetFilter, filterBy, resetFilter, schoolYears }) => {
  const initialFilter = {
    search: '',
    school_year_id: ''
  };
  const [form, setForm] = useState(initialFilter);

  const onChange = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
    filterBy(value, target);
  }

  return (
    <section className="mb-4 grid sm:flex grid-cols-2 lg:flex-row items-end gap-4">
      <Input
        label="Nombre o código de la materia"
        className='w-[20rem]'
        labelPlacement='outside'
        placeholder="Buscar por nombre o código"
        startContent={<IconSearch />}
        variant="bordered"
        radius="sm"
        value={form.search}
        onValueChange={v => onChange(v, 'search')}
      />

      <Select
        label="Año escolar"
        labelPlacement="outside"
        placeholder=" "
        variant="bordered"
        className='w-[15rem]'
        radius="sm"
        classNames={{ label: 'font-semibold' }}
        disallowEmptySelection={true}
        selectedKeys={!!form.school_year_id ? [form.school_year_id] : []}
        onSelectionChange={v => onChange(v.currentKey, 'school_year_id')}
      >
        {schoolYears.map((item) => (
          <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
        ))}
      </Select>

      {canResetFilter && (
        <Button
          variant="light"
          className="text-primaryDark"
          onPress={() => {
            setForm(initialFilter);
            resetFilter();
          }}
        >
          Limpiar filtros
        </Button>
      )}
    </section>
  )
}

const useFetchTable = () => {
  const initialFilters = {
    page: 1,
    perPage: Constants.PER_PAGE,
    search: '',
    school_year_id: ''
  };

  const initialPagination = {
    page: 1,
    pages: 1,
    total: 0,
    perPage: Constants.PER_PAGE,
  };

  const user = useSelector(state => state.user);
  const [data, setData] = useState([]);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState(initialPagination);
  const [schoolYears, setSchoolYears] = useState([]);

  const debounceTime = 500;
  const debounce = useRef();

  const toSelect = (items) => items.map(x => ({ ...x, value: String(x.id), label: x.name }));


  const fetchData = async () => {
    setData([]);
    if (!canFetch) return;
    setCanFetch(false);

    try {
      const response = await TeacherTestsService.tests.getTests({ ...filters, professor_id: user.id });
      const { data, ...rest } = response;

      setData(data);
      setPagination(rest);
      setCanFetch(true);

    } catch (error) {
      setData([]);
      onError(String(error));
    }
  }

  const download = async () => {
    setCanFetch(false);
    try {
      const response = await TeacherTestsService.tests.getTests({ ...filters, professor_id: user.id, download: true  });
      if (response.url) {
        const link = document.createElement('a');
        link.href = response.url;
        link.download = response.url.split('/').pop();
        link.target = '_blank';
        link.click();
        link.remove();
      }
      setCanFetch(true);
    } catch (error) {
      onError(String(error));
    }
  }

  const getSelects = async () => {
    try {
      const selects = await TeacherAttendeceService.attendence.getSelects({ professor_id: user.id });
      setSchoolYears(toSelect(selects.school_years));
      setCanFetch(true)

    } catch (error) {
      setData([]);
      onError(String(error));
    }
  }

  const deleteEvaluation = async (id) => {
    try {
      setCanFetch(false);
      await TeacherTestsService.tests.destroyTest({ id });
      setCanFetch(true);
      toast.success('Evaluación eliminada con éxito');
    } catch (error) {
      toast.error(String(error) || "Ocurrió un error inesperado");
      setCanFetch(true);
    }
  }

  const getCurrentPagination = () => {
    let pag;
    setPagination(s => {
      pag = s;
      return s;
    });
    return pag;
  }

  const canResetFilter = () => {
    const { page, perPage, ...initial } = initialFilters;
    const { page: _, perPage: __, ...current } = filters;
    const initFilter = JSON.stringify(initial);
    const currFilter = JSON.stringify(current);
    return initFilter !== currFilter;
  }

  const onError = (msg) => toast.error(msg);

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    else fetchData();
  }

  const goToPage = (page) => {
    const pagination = getCurrentPagination();
    if (page >= 1 && page <= pagination.pages && page !== pagination.page) {
      setCanFetch(true);
      setFilters({ ...filters, page });
    }
  }

  const changePerPage = (perPage) => {
    setCanFetch(true);
    setFilters({ ...filters, perPage });
  }

  const filterBy = (value, target) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setCanFetch(true);
      setFilters({ ...filters, page: 1, [target]: value });
    }, debounceTime);
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  useEffect(() => {
    getSelects()
  }, [])

  return {
    canResetFilter: canResetFilter(),
    changePerPage,
    data,
    filterBy,
    filters,
    goToPage,
    isLoading: !canFetch,
    pagination,
    reload,
    deleteEvaluation,
    schoolYears,
    download,
  }
}

export default ClassManagement;
